import { useGlobalStore } from '@/store'
import { useTheme } from 'vuetify'
import { cookieRef } from '@layouts/stores/config'
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile'

const toast = useToast()
const filterNumRegExp = /[^\d]/g
export const hasOwnProperty = (obj, prop) => obj.hasOwnProperty(prop)
export const getLocalStorage = key => localStorage.getItem(key)
export const setLocalStorage = (key, value) => localStorage.setItem(key, value)
export const removeLocalStorage = key => localStorage.removeItem(key)
export const jsonStringify = JSON.stringify
export const jsonParse = JSON.parse
export const isEmpty = value => {
  if (value === null || value === undefined || value === '') return true
  
  return !!isEmptyArray(value)
}
export const isEmptyArray = arr => isArray(arr) && arr.length === 0 
export const isNullOrUndefined = value => value === null || value === undefined
export const isObject = value => Object.prototype.toString.call(value) === '[object Object]'
export const isArray = value => Object.prototype.toString.call(value) === '[object Array]'
export const isBlob = value => {
  if (!(value instanceof Blob)) {
    return false
  }
  
  return value.type.startsWith('image/')
}
export const stripAccountNumberDashes = value => String(value ?? '').replace(/-/g, '')
export const strLength = value => {
  if (isEmpty(value)) return
  
  return String(value).length
}
export const getApiBaseUrl = () => import.meta.env.VITE_API_BASE_URL || '/api'
export const getClientId = () => {
  const channel = (localStorage.getItem('channelName') || 'A55').trim().toUpperCase()
  switch (channel) {
  case 'A55':
    return import.meta.env.VITE_A55_WEB_CLIENT_ID
  case 'TRANSFER':
    return import.meta.env.VITE_TRANSFER_WEB_CLIENT_ID
  case 'BLU':
  default:
    return import.meta.env.VITE_BLU_WEB_CLIENT_ID
  }
}
export const COPY_SUCCESS_TOAST_ID = 'clipboard-copy-success'
export const COPY_ERROR_TOAST_ID = 'clipboard-copy-error'

export const copyToClipboard = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text)
    if (successMessage) {
      // Keep at most one copy success toast visible.
      toast.success(successMessage, { id: COPY_SUCCESS_TOAST_ID })
    }
    return true
  } catch (err) {
    toast.error('Copying failed', { id: COPY_ERROR_TOAST_ID })
    return false
  }
}
export const sliceStr = (str, len = 255) => {
  if (str === null || str === undefined) {
    return ''
  }
  return String(str).slice(0, len)
}

export const isFetchBankOptionsSuccessful = () => {
  const globalStore = useGlobalStore()
  const ispbOptions = globalStore.ispbOptions.filter(item => item.value !== '0')

  return ispbOptions.length < 1 ? false : true
}
export const sleep = (ms = 50) => new Promise(resolve => setTimeout(resolve, ms))
export const arrayDeduplication = arr => [...new Set(arr.map(JSON.stringify))].map(JSON.parse)
export const isDarkTheme = () => {
  const theme = useTheme()
  const isDarkTheme = computed(() => theme.global.current.value.dark)
  return isDarkTheme.value
}
export const getUserInfoSync = () => {
  // try {
  //   const cachedUserInfo = getItem('globalUserInfo', 'datasource')
  //   if (cachedUserInfo) {
  //     userInfo = cachedUserInfo
  //   }
  // } catch (error) {
  //   console.error('Error loading user info from cache:', error)
  // }
  return {
    currency: 'BRL'
  }
}
export const getCurrentLocale = () => {
  return 'pt'
  let locale = (navigator.language || navigator.browserLanguage).toLowerCase()
  if (locale.includes('pt-')) {
    return 'pt'
  }
  return 'en'
}
export const getCurrentLanguage = () => {
  try {
    const storedLang = cookieRef('language', 'pt')
    return storedLang.value || getCurrentLocale()
  } catch (error) {
    return getCurrentLocale()
  }
}
export const getUserInfo = async () => {
  const globalStore = useGlobalStore()
  let userInfo = {
    currency: 'BRL'
  }
  try {
    const cachedUserInfo = getItem('globalUserInfo', 'datasource')
    if (cachedUserInfo) {
      userInfo = cachedUserInfo
    } else {
      userInfo = await globalStore.getGlobalUserInfo()
    }
  } catch (error) {
    console.error('Error loading user info:', error)
  }
  return userInfo
}
export const amountInput = (value, maxAmount = null) => {
  if (!value) {
    return ''
  }
  const userInfo = getUserInfoSync()
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    if (value.endsWith('.') && !value.includes(',')) {
      value = value.slice(0, -1) + ','
    }
    value =  value.replace(/[^\d,]/g, '')
    value = value.replace(/\,{2,}/g, ',')
    value = value.replace(',', '.')
  } else {
    value =  value.replace(/[^\d.]/g, '')
    value = value.replace(/\.{2,}/g, '.')
  } 
  const match = value.match(/^\d*(\.?\d{0,2})/g)
  if (match) {
    value = match[0]
  }
  if (maxAmount && parseFloat(value) > maxAmount) {
    value = maxAmount.toFixed(2)
  }
  return value
}

/** Percentage fee weight: BRL can use "," or "." as decimal while typing; min/max amount also uses "amountInput" with dot in model. Up to 3 fraction digits. */
export const percentageWeightInput = value => {
  if (!value) {
    return ''
  }
  const userInfo = getUserInfoSync()
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    const s = String(value)
    // If user types a dot as decimal and no comma yet, do NOT strip the dot (e.g. "1.2" not "12")
    if (s.includes('.') && !s.includes(',')) {
      const digitsAndDots = s.replace(/[^\d.]/g, '')
      const i = digitsAndDots.indexOf('.')
      if (i === -1) {
        return digitsAndDots
      }
      return digitsAndDots.slice(0, i + 1) + digitsAndDots.slice(i + 1).replace(/\D/g, '').slice(0, 3)
    }
    if (s.endsWith('.') && !s.includes(',')) {
      value = s.slice(0, -1) + ','
    } else {
      value = s
    }
    value = String(value).replace(/[^\d,]/g, '')
    value = value.replace(/\,{2,}/g, ',')
    value = value.replace(',', '.')
  } else {
    value = String(value).replace(/[^\d.]/g, '')
    value = value.replace(/\.{2,}/g, '.')
  }
  const match = value.match(/^\d*(\.?\d{0,3})/g)
  return match ? match[0] : ''
}
export const formatAmountDisplay = value => {
  if (!value && value !== 0) {
    return ''
  }
  const numStr = String(value).replace(/,/g, '')
  const num = parseFloat(numStr)
  if (isNaN(num)) {
    return value
  }
  const userInfo = getUserInfoSync()
  const hasDecimal = numStr.includes('.')
  if (hasDecimal) {
    const parts = numStr.split('.')
    const integerPart = parseInt(parts[0] || '0', 10)
    const decimalPart = parts[1] || ''
    if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
      return integerPart.toLocaleString('pt-BR') + ',' + decimalPart
    } else {
      return integerPart.toLocaleString('en-US') + '.' + decimalPart
    }
  } else {
    if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
      return num.toLocaleString('pt-BR')
    } else {
      return num.toLocaleString('en-US')
    }
  }
}
export const formatAmountWithCommas = value => {
  if (!value && value !== 0) {
    return ''
  }
  const userInfo = getUserInfoSync()
  let numStr = ''
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    if(detectAmountFormat(value) === 'pt-BR') {
      numStr = String(value).replace(/\./g, '').replace(',','.')
    }else{
      numStr = String(value).replace(/,/g, '')
    }
  } else {
    numStr = String(value).replace(/,/g, '')
  }
  const num = parseFloat(numStr)
  if (isNaN(num)) {
    return value
  }
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  } else {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }
}

/** Fee list table: percent charge weight shows up to 3 decimal places; fixed uses currency-style formatting. */
export const formatFeeTableChargeWeight = (chargeWeight, chargeType) => {
  if (chargeType !== 'PERCENTAGE') {
    return formatAmountWithCommas(chargeWeight)
  }
  if (!chargeWeight && chargeWeight !== 0) {
    return ''
  }
  const userInfo = getUserInfoSync()
  let numStr = ''
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    if (detectAmountFormat(chargeWeight) === 'pt-BR') {
      numStr = String(chargeWeight).replace(/\./g, '').replace(',', '.')
    } else {
      numStr = String(chargeWeight).replace(/,/g, '')
    }
  } else {
    numStr = String(chargeWeight).replace(/,/g, '')
  }
  const num = parseFloat(numStr)
  if (isNaN(num)) {
    return chargeWeight
  }
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })
}

export const detectAmountFormat = value => {
  if (!value && value !== 0) {
    return null
  }
  const str = String(value).trim()
  if (!str.includes(',') && !str.includes('.')) {
    return null
  }

  const lastCommaIndex = str.lastIndexOf(',')
  const lastDotIndex = str.lastIndexOf('.')
  if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
    const afterComma = str.substring(lastCommaIndex + 1).replace(/\s/g, '')
    const afterDot = str.substring(lastDotIndex + 1).replace(/\s/g, '')
    const commaDigits = afterComma.replace(/\D/g, '').length
    const dotDigits = afterDot.replace(/\D/g, '').length
    if (lastCommaIndex > lastDotIndex && commaDigits <= 2 && commaDigits > 0) {
      return 'pt-BR'
    }
    if (lastDotIndex > lastCommaIndex && dotDigits <= 2 && dotDigits > 0) {
      return 'en-US'
    }    
    if (commaDigits <= 2 && dotDigits > 3) {
      return 'pt-BR'
    }
    if (dotDigits <= 2 && commaDigits > 3) {
      return 'en-US'
    }
    return lastCommaIndex > lastDotIndex ? 'pt-BR' : 'en-US'
  }
  if (lastCommaIndex !== -1) {
    const afterComma = str.substring(lastCommaIndex + 1).replace(/\s/g, '')
    const commaDigits = afterComma.replace(/\D/g, '').length
    if (commaDigits <= 2 && commaDigits > 0) {
      return 'pt-BR'
    }
    return 'en-US'
  }
  if (lastDotIndex !== -1) {
    const afterDot = str.substring(lastDotIndex + 1).replace(/\s/g, '')
    const dotDigits = afterDot.replace(/\D/g, '').length
    if (dotDigits <= 2 || dotDigits > 3) {
      return 'en-US'
    }
    return 'pt-BR'
  }
  
  return null
}
export const parseAmountFromFormatted = value => {
  if (!value) {
    return ''
  }
  const userInfo = getUserInfoSync()
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    return String(value).replace(/\./g, '').replace(',', '.')
  } else {
    return String(value).replace(/,/g, '')
  }
}

/**
 * % weight in the add-fee form: the model can be "0.233" (dot) while typing, but
 * `parseAmountFromFormatted` in BRL removes *all* dots, producing "0233" → 233. Use this instead.
 * When both `,` and `.` appear, BRL money rules apply; dot-only = decimal separator.
 */
export const parsePercentageWeightValue = value => {
  if (value === '' || value == null) {
    return Number.NaN
  }
  const s = String(value).trim()
  if (s === '') {
    return Number.NaN
  }
  const hasComma = s.includes(',')
  const hasDot = s.includes('.')
  if (hasDot && !hasComma) {
    return parseFloat(s.replace(/[^\d.]/g, ''))
  }
  return parseFloat(parseAmountFromFormatted(s))
}

/** Add fee % weight field blur: locale-formatted string, max 3 decimal places. */
export const formatPercentageWeightFormBlur = value => {
  if (!value && value !== 0) {
    return ''
  }
  const num = parsePercentageWeightValue(String(value))
  if (isNaN(num)) {
    return value
  }
  const userInfo = getUserInfoSync()
  if (userInfo || userInfo.currency || userInfo.currency === '' || userInfo.currency === 'BRL') {
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })
}

export const formatAccount = account => {
  if (!account || (typeof account === 'string' && account.trim().length === 0)) {
    return ""
  }
  const accountStr = String(account)
  if (accountStr.length === 0) {
    return ""
  }
  let len = accountStr.length
  return `${accountStr.substring(0, len - 1)}-${accountStr.substring(len - 1)}`
}
export const formatCpf = cpf => {
  if (!cpf || (typeof cpf === 'string' && cpf.trim().length === 0)) {
    return ""
  }
  cpf = String(cpf).replace(filterNumRegExp, '')
  let str = ""
  for (let i = 0; i < cpf.length; i++) {
    if (i == 3) {
      str = `${str}.${cpf[i]}`
      continue
    }
    if (i == 6) {
      str = `${str}.${cpf[i]}`
      continue
    }
    if (i == 9) {
      str = `${str}-${cpf[i]}`
      continue
    }
    str = str + cpf[i]
  }
  return str
}
export const formatCnpj = cnpj => {
  if (!cnpj || (typeof cnpj === 'string' && cnpj.trim().length === 0)) {
    return ""
  }
  cnpj = String(cnpj).replace(filterNumRegExp, '')
  let str = ""
  for (let i = 0; i < cnpj.length; i++) {
    if (i == 2) {
      str = `${str}.${cnpj[i]}`
      continue
    }
    if (i == 5) {
      str = `${str}.${cnpj[i]}`
      continue
    }
    if (i == 8) {
      str = `${str}/${cnpj[i]}`
      continue
    }
    if (i == 12) {
      str = `${str}-${cnpj[i]}`
      continue
    }
    str = str + cnpj[i]
  }
  return str
}
export const validateCpf = cpfVal => {
  if (!cpfVal || (typeof cpfVal === 'string' && cpfVal.trim().length === 0)) {
    return false
  }
  cpfVal = String(cpfVal).replace(filterNumRegExp, '')
  if (!cpfVal || cpfVal.length === 0) {
    return false
  }
  const check = /^[0-9]{11}$/
  if (!check.test(cpfVal)) {
    return false
  }
  let add = 0
  for (let i = 0; i < 9; i++) {
    add += Number(cpfVal[i]) * (10 - i)
  }
  let rev = 11 - (add % 11)
  if (rev == 10 || rev == 11) {
    rev = 0
  }
  if (rev != Number(cpfVal[9])) {
    return false
  }
  add = 0
  for (let i = 0; i < 10; i++) {
    add += Number(cpfVal[i]) * (11 - i)
  }
  rev = 11 - (add % 11)
  if (rev == 10 || rev == 11) {
    rev = 0
  }
  return !(rev != Number(cpfVal[10]))
}
export const validateCnpj = val => {
  if (!val || (typeof val === 'string' && val.trim().length === 0)) {
    return false
  }
  val = String(val).replace(filterNumRegExp, '')
  if (!val || val.length === 0) {
    return false
  }
  const check = /^[0-9]{14}$/
  if (!check.test(val)) {
    return false
  }
  let tamanho = val.length - 2
  const numeros = val.substring(0, tamanho)
  const digitos = val.substring(tamanho)
  let soma = 0
  let pos = tamanho - 7
  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros[tamanho - i]) * pos--
    if (pos < 2) pos = 9
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11
  if (resultado != Number(digitos[0])) {
    return false
  }
  tamanho = tamanho + 1
  const numeros2 = val.substring(0, tamanho)
  soma = 0
  pos = tamanho - 7
  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros2[tamanho - i]) * pos--
    if (pos < 2) pos = 9
  }
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11
  return !(resultado != Number(digitos[1]))
}
export const showDocumentId = documentId => {
  let str = documentId
  if (validateCnpj(documentId)) {
    str = formatCnpj(documentId)
  } else if (validateCpf(documentId)) {
    str = formatCpf(documentId)
  }
  return str
}

/** document_type from API: string CPF/CNPJ or enum 1=CPF, 2=CNPJ */
export const isCnpjDocumentType = documentType => {
  const typeStr = String(documentType ?? '').trim().toUpperCase()
  const typeNum = Number(documentType)
  return typeStr === 'CNPJ' || typeNum === 2
}

/** Format document digits using document_type (string CPF/CNPJ or enum 1=CPF, 2=CNPJ). */
export const formatDocumentIdByType = (documentId, documentType) => {
  const digits = String(documentId ?? '').replace(filterNumRegExp, '')
  if (!digits) {
    return ''
  }

  const typeStr = String(documentType ?? '').trim().toUpperCase()
  const typeNum = Number(documentType)

  if (typeStr === 'CNPJ' || typeNum === 2) {
    const formatted = formatCnpj(digits)
    return formatted || digits
  }
  if (typeStr === 'CPF' || typeNum === 1) {
    const formatted = formatCpf(digits)
    return formatted || digits
  }

  return showDocumentId(digits) || digits
}
export const hypoDocumentId = documentId => {
  let str = documentId
  if (validateCnpj(documentId)) {
    str = hypoCNPJ(documentId)
  } else if (validateCpf(documentId)) {
    str = hypoCPF(documentId)
  }
  return str
}
export const hypoCPF = cpf => {
  return `***${cpf.substring(3, 12)}**`
}
export const hypoCNPJ = cnpj => {
  return `**${cnpj.substring(2, 16)}**`
}
export const formatPhone = phone => {
  if (!phone || phone.length === 0) {
    return ''
  }

  const digits = phone.replace(filterNumRegExp, '')
  if (!digits.length) {
    return ''
  }

  const parsed = parsePhoneNumberFromString(`+${digits}`)
  if (parsed) {
    return parsed.formatInternational()
  }

  return `+${digits}`
}
export const hypoPhone = phone => {
  let _phone = ""
  if (!phone || phone.length === 0) {
    return _phone
  }
  _phone = phone.replace(filterNumRegExp, "")
  if (!_phone || _phone.length === 0 || _phone.length < 11) {
    return _phone
  }
  if (_phone.length >= 13) {
    _phone = _phone.substring(2)
  }
  _phone = `${_phone.substring(0, 3)} **** ${_phone.substring(9)}`
  return _phone
}
export const showCEP = cep => {
  let str = ""
  if (!cep || cep.length === 0) {
    return str
  }
  cep = cep.replace(filterNumRegExp, "")
  if (!cep || cep.length === 0) {
    return str
  }
  for (let i = 0; i < cep.length; i++) {
    if (i == 5) {
      str = `${str}-${cep[i]}`
      continue
    }
    str = str + cep[i]
  }
  return str
}
export const subtractOneMonth = inputDate => {
  let resultDate = new Date(inputDate.year, inputDate.month - 1, inputDate.day)
  if (resultDate.month == 0) {
    resultDate = new Date(inputDate.year - 1, 11, inputDate.day)
  }
  return resultDate
}
export const calculateFee = (amount, feeConfig) => {
  if (!feeConfig || !feeConfig.charge_type) {
    return 0
  }
  const { charge_type, charge_weight, min_amount, max_amount } = feeConfig
  const chargeWeight = parseFloat(charge_weight || 0)
  const minAmount = parseFloat(min_amount || 0)
  const maxAmount = parseFloat(max_amount || Infinity)
  const paymentAmount = parseFloat(amount || 0)

  let fee = 0
  if (charge_type === 'FIXED') {
    fee = chargeWeight
  } else {
    fee = paymentAmount * chargeWeight / 100
    if (fee < minAmount) {
      fee = minAmount
    } else if (fee > maxAmount) {
      fee = maxAmount
    }
  }
  return fee
}
export const shouldShowFeeInfo2 = (amount, feeConfig) => {
  if (!amount || !feeConfig || !feeConfig.charge_type) return false
  const parsedAmount = parseAmountFromFormatted(amount)
  const numAmount = parseFloat(parsedAmount) || 0
  return numAmount > 0
}
export const extractDocumentType = value => {
  if (!value) return ''
  return value.replace('DOCUMENT_TYPE_', '')
}
export const extractAccountType = value => {
  if (!value) return ''
  return value.replace('BANK_ACCOUNT_TYPE_', '')
}
export const extractAccountStatus = value => {
  if (!value) return ''
  return value.replace('BANK_ACCOUNT_STATUS_', '').toLowerCase()
}
export const isAccountStatusActive = status => {
  const statusValue = extractAccountStatus(status)
  return statusValue === 'active'
}
export const safePixKeys = pixKeys => {
  if (!pixKeys || !Array.isArray(pixKeys)) return []
  return pixKeys.filter(key => key !== null && key !== undefined)
}
export const formatPixKeyType = pixKeyType => {
  if (!pixKeyType) return ''
  return pixKeyType.replace('PIX_KEY_TYPE_', '').toUpperCase()
}
export const extractTradeType = value => {
  if (!value) return ''
  return value.replace('TXN_TYPE_', '')
}
export const extractStatus = value => {
  if (!value) return ''
  return value.replace('TXN_STATUS_', '')
}
export const extractFlowDirection = value => {
  if (!value) return ''
  return value.replace('FLOW_DIRECTION_', '')
}
export const isIssueCaseHandled = row => {
  const v = row?.finished_at
  if (v == null || v === '' || v === 0 || v === '0') return false
  const n = Number(v)
  return !Number.isNaN(n) && n !== 0
}
export const ISSUE_TYPE_SETTLEMENT_RECONCILE = 1
export const ISSUE_TYPE_SETTLEMENT_TIMEOUT = 2
export const FLOW_TYPE_IN = 1
export const FLOW_TYPE_OUT = 2
export const FLOW_TYPE_TRANSFER = 3
const ERROR_CASE_ISSUE_TYPE_I18N = {
  [ISSUE_TYPE_SETTLEMENT_RECONCILE]: 'errorCasesTable.issueTypeSettlementReconcile',
  [ISSUE_TYPE_SETTLEMENT_TIMEOUT]: 'errorCasesTable.issueTypeSettlementTimeout',
}
const ERROR_CASE_FLOW_TYPE_I18N_NUMERIC = {
  [FLOW_TYPE_IN]: 'transactions.In',
  [FLOW_TYPE_OUT]: 'transactions.Out',
  [FLOW_TYPE_TRANSFER]: 'transactions.Transfer',
}
const ERROR_CASE_FLOW_TYPE_I18N_STRING = {
  IN: 'transactions.In',
  OUT: 'transactions.Out',
  TRANSFER: 'transactions.Transfer',
}
const ERROR_CASE_TRADE_TYPE_LABEL = {
  1: 'PAYMENT',
  2: 'REFUND',
  3: 'TRANSFER',
  4: 'WITHDRAWAL',
  5: 'DEPOSIT',
}
const errorCaseTradeTypeFromNumeric = value => {
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return ERROR_CASE_TRADE_TYPE_LABEL[n] ?? null
}
export const resolveErrorCaseTradeType = row => {
  const raw = row?.trade_type ?? row?.txn_type
  if (raw == null || raw === '') return undefined
  const fromNum = errorCaseTradeTypeFromNumeric(raw)
  if (fromNum) return fromNum
  const s = String(raw).trim()
  if (!s) return undefined
  if (s.startsWith('TXN_TYPE_')) return extractTradeType(s)
  return s
}
export const resolveErrorCaseIssueTypeI18nKey = value => {
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return ERROR_CASE_ISSUE_TYPE_I18N[n] ?? null
}
export const resolveErrorCaseFlowTypeI18nKey = value => {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isNaN(n) && ERROR_CASE_FLOW_TYPE_I18N_NUMERIC[n]) {
    return ERROR_CASE_FLOW_TYPE_I18N_NUMERIC[n]
  }
  const s = String(value).trim().toUpperCase()
  return ERROR_CASE_FLOW_TYPE_I18N_STRING[s] ?? null
}
export const TASK_STATUS_ERROR = 1
export const TASK_STATUS_SUCCESS = 2
export const TASK_STATUS_CLOSED = 3
export const TASK_STATUS_PROCESSING = 4
export const TASK_STATUS_BLOCKED = 5

const ERROR_CASE_TASK_STATUS_I18N = {
  [TASK_STATUS_ERROR]: 'errorCasesTable.taskStatusError',
  [TASK_STATUS_SUCCESS]: 'errorCasesTable.taskStatusSuccess',
  [TASK_STATUS_CLOSED]: 'errorCasesTable.taskStatusClosed',
  [TASK_STATUS_PROCESSING]: 'errorCasesTable.taskStatusProcessing',
  [TASK_STATUS_BLOCKED]: 'errorCasesTable.taskStatusBlocked',
}
export const resolveErrorCaseTaskStatusI18nKey = value => {
  if (value == null || value === '') return null
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return ERROR_CASE_TASK_STATUS_I18N[n] ?? null
}
export const ERROR_CASE_TASK_STATUS_VALUES = [
  TASK_STATUS_ERROR,
  TASK_STATUS_SUCCESS,
  TASK_STATUS_CLOSED,
  TASK_STATUS_PROCESSING,
  TASK_STATUS_BLOCKED,
]
export const extractFeeType = value => {
  if (!value) return ''
  return value.replace('PIX_', '')
}
export const getDocumentPics = userDetail => {
  if (!userDetail?.basic_info?.user_info?.document_pic) {
    return { front: null, back: null }
  }
  const docPicData = userDetail.basic_info.user_info.document_pic
  return {
    front: docPicData?.doc_pic_front && docPicData.doc_pic_front !== '' ? docPicData.doc_pic_front : null,
    back: docPicData?.doc_pic_back && docPicData.doc_pic_back !== '' ? docPicData.doc_pic_back : null,
  }
}
export const getDocumentType = userDetail => {
  try {
    const documentType = userDetail?.basic_info?.user_info?.document_type
    if (documentType === 'CPF' || documentType === 'CNPJ') {
      return documentType
    }
    return 'CPF'
  } catch (error) {
    console.error('Error getting document type:', error)
    return 'CPF'
  }
}